import { CardElement, DigitalPassport, OnlinePlayer } from "../types/battle"
import {
  MapPlayerDetailResponse,
  MapPlayerPresence,
  MapPlayerProfilePreview,
  MapPlayersResponse,
} from "../types/location"
import { PublicProfileCard } from "../types/publicProfile"
import { calcBaseBS } from "../constants/game.constants"
import { normalizeBattlePlayerInfo } from "./challengePayload"
import { normalizeAvailableRanks } from "./radarStatus"
import { normalizeGameCard, normalizePvpStats } from "./gameCards"
import { normalizeCardElement } from "./cardElement"
import { distanceMeters } from "./geo"

function pickNumber(value: unknown, fallback = NaN): number {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim()) {
    const n = Number(value)
    return Number.isFinite(n) ? n : fallback
  }
  return fallback
}

function pickBool(value: unknown, fallback = false): boolean {
  if (typeof value === "boolean") return value
  if (value === 1 || value === "1" || value === "true") return true
  if (value === 0 || value === "0" || value === "false") return false
  return fallback
}

function normalizeProfilePreviewCard(raw: unknown): PublicProfileCard | null {
  const card = normalizeGameCard(raw)
  if (!card) return null
  const obj =
    raw && typeof raw === "object" && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {}
  const baseBS = pickNumber(obj.baseBS ?? obj.base_bs, calcBaseBS(card))
  const effectiveBS = pickNumber(obj.effectiveBS ?? obj.effective_bs, baseBS)
  return {
    ...card,
    baseBS,
    effectiveBS,
    isActive: pickBool(obj.isActive ?? obj.is_active),
  }
}

function pickString(value: unknown, fallback = ""): string {
  if (typeof value === "string") return value
  if (value != null) return String(value)
  return fallback
}

export function normalizeMapPlayer(raw: unknown): MapPlayerPresence | null {
  const base = normalizeBattlePlayerInfo(raw)
  if (!base) return null

  const obj =
    raw && typeof raw === "object" && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {}

  const lat = pickNumber(obj.lat ?? obj.latitude ?? obj.playerLat ?? obj.player_lat)
  const lng = pickNumber(obj.lng ?? obj.longitude ?? obj.playerLng ?? obj.player_lng)
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null

  const availableRanks =
    normalizeAvailableRanks(obj.availableRanks ?? obj.available_ranks) ?? []

  const accuracyRaw = obj.accuracyM ?? obj.accuracy_m
  const accuracyM =
    accuracyRaw == null ? null : pickNumber(accuracyRaw, NaN)
  const cityIdRaw = obj.cityId ?? obj.city_id
  const cityId =
    cityIdRaw == null ? undefined : pickNumber(cityIdRaw, NaN)

  return {
    ...base,
    cityId: Number.isFinite(cityId) ? cityId : undefined,
    affiliationElement: normalizeCardElement(
      obj.affiliationElement ?? obj.affiliation_element
    ),
    avatarVariant: (() => {
      const raw = obj.avatarVariant ?? obj.avatar_variant
      if (raw == null || raw === "") return base.avatarVariant ?? 1
      const n = Math.floor(pickNumber(raw, 1))
      return n >= 1 && n <= 3 ? n : 1
    })(),
    lat,
    lng,
    accuracyM: Number.isFinite(accuracyM) ? accuracyM : null,
    updatedAt: pickString(obj.updatedAt ?? obj.updated_at) || undefined,
    distanceM: (() => {
      const dist = pickNumber(obj.distanceM ?? obj.distance_m)
      return Number.isFinite(dist) ? dist : undefined
    })(),
    availableRanks,
    isOnline: pickBool(obj.isOnline ?? obj.is_online, true),
    isOnMap: pickBool(obj.isOnMap ?? obj.is_on_map, true),
    trail: normalizeMapPlayerTrail(obj.trail ?? obj.points),
  }
}

function normalizeMapPlayerTrail(raw: unknown): MapPlayerPresence["trail"] {
  if (!Array.isArray(raw) || raw.length === 0) return undefined
  const points = raw
    .map((item) => {
      if (!item || typeof item !== "object" || Array.isArray(item)) return null
      const obj = item as Record<string, unknown>
      const pointLat = pickNumber(obj.lat ?? obj.latitude)
      const pointLng = pickNumber(obj.lng ?? obj.longitude)
      const ageSec = Math.floor(pickNumber(obj.ageSec ?? obj.age_sec, NaN))
      if (
        !Number.isFinite(pointLat) ||
        !Number.isFinite(pointLng) ||
        !Number.isFinite(ageSec) ||
        ageSec < 0
      ) {
        return null
      }
      return {
        lat: pointLat,
        lng: pointLng,
        ageSec,
        recordedAt: pickString(obj.recordedAt ?? obj.recorded_at) || undefined,
      }
    })
    .filter((point): point is NonNullable<typeof point> => point != null)

  if (!points.length) return undefined
  return points.sort((a, b) => b.ageSec - a.ageSec)
}

function normalizeMapPlayersMeta(raw: unknown): MapPlayersResponse["meta"] {
  const obj =
    raw && typeof raw === "object" && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {}
  return {
    radiusM: (() => {
      const value = pickNumber(obj.radiusM ?? obj.radius_m)
      return Number.isFinite(value) ? value : undefined
    })(),
    total: (() => {
      const value = pickNumber(obj.total)
      return Number.isFinite(value) ? value : undefined
    })(),
    ttlSec: (() => {
      const value = pickNumber(obj.ttlSec ?? obj.ttl_sec)
      return Number.isFinite(value) ? value : undefined
    })(),
  }
}

export function normalizeMapPlayersResponse(data: unknown): MapPlayersResponse {
  const obj =
    data && typeof data === "object" && !Array.isArray(data)
      ? (data as Record<string, unknown>)
      : {}
  const nested = obj.data ?? obj.result ?? obj.payload
  const source =
    nested && typeof nested === "object" && !Array.isArray(nested)
      ? (nested as Record<string, unknown>)
      : obj

  const rawList = Array.isArray(source.players) ? source.players : []
  const players = rawList
    .map(normalizeMapPlayer)
    .filter((player): player is MapPlayerPresence => player != null)

  return {
    players,
    meta: normalizeMapPlayersMeta(source.meta),
  }
}

function normalizeMapPlayerProfilePreview(
  raw: unknown
): MapPlayerProfilePreview | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined
  const obj = raw as Record<string, unknown>
  const activeCardRaw = obj.activeCard ?? obj.active_card
  const activeCard = activeCardRaw
    ? normalizeProfilePreviewCard(activeCardRaw)
    : null

  const effectiveBSRaw = obj.effectiveBS ?? obj.effective_bs
  const effectiveBS = pickNumber(effectiveBSRaw)

  return {
    cardsCount: (() => {
      const value = pickNumber(obj.cardsCount ?? obj.cards_count)
      return Number.isFinite(value) ? value : undefined
    })(),
    effectiveBS: Number.isFinite(effectiveBS) ? effectiveBS : undefined,
    activeCard,
    pvpStats: normalizePvpStats(obj.pvpStats ?? obj.pvp_stats),
  }
}

export function normalizeMapPlayerDetailResponse(
  data: unknown
): MapPlayerDetailResponse | null {
  const obj =
    data && typeof data === "object" && !Array.isArray(data)
      ? (data as Record<string, unknown>)
      : {}
  const nested = obj.data ?? obj.result ?? obj.payload
  const source =
    nested && typeof nested === "object" && !Array.isArray(nested)
      ? (nested as Record<string, unknown>)
      : obj

  const player = normalizeMapPlayer(source.player ?? source)
  if (!player) return null

  return {
    player,
    profilePreview: normalizeMapPlayerProfilePreview(
      source.profilePreview ?? source.profile_preview
    ),
  }
}

export function mapPlayerToOnlinePlayer(player: MapPlayerPresence): OnlinePlayer {
  return {
    userId: player.userId,
    nickname: player.nickname,
    publicGameCode: player.publicGameCode,
    globalPts: player.globalPts,
    localPts: player.localPts,
    academicLeague: player.academicLeague,
    guardianPath: player.guardianPath ?? null,
    affiliationElement: player.affiliationElement ?? null,
    avatarVariant: player.avatarVariant ?? 1,
    profileFrameKey: player.profileFrameKey ?? null,
    cityName: player.cityName,
    guildTag: player.guildTag ?? null,
    guildName: player.guildName ?? null,
    guildEmblemKey: player.guildEmblemKey ?? null,
    availableRanks: player.availableRanks ?? [],
  }
}

export function buildSelfMapPlayerDetail(
  passport: DigitalPassport,
  coords: { lat: number; lng: number } | null
): MapPlayerDetailResponse {
  const activeCard = passport.activeCard
    ? normalizeProfilePreviewCard(passport.activeCard)
    : null

  return {
    player: {
      userId: passport.user_id,
      nickname: passport.nickname,
      publicGameCode: passport.publicGameCode,
      globalPts: passport.globalPts,
      localPts: passport.localPts,
      academicLeague: passport.academicLeague,
      guardianPath: passport.guardianPath,
      profileFrameKey: passport.profileFrameKey,
      cityName: passport.city?.title ?? null,
      cityId: passport.city_id ?? passport.city?.city_id ?? undefined,
      affiliationElement: passport.affiliationElement,
      avatarVariant: passport.avatarVariant ?? 1,
      guildTag: passport.guild?.tag ?? null,
      guildName: passport.guild?.name ?? null,
      guildEmblemKey: passport.guild?.emblemKey ?? null,
      lat: coords?.lat ?? 0,
      lng: coords?.lng ?? 0,
      distanceM: 0,
      availableRanks: [],
      isOnline: true,
      isOnMap: true,
    },
    profilePreview: {
      activeCard,
      effectiveBS: activeCard?.effectiveBS,
      pvpStats: passport.pvpStats,
    },
  }
}

export function upsertMapPlayer(
  list: MapPlayerPresence[],
  next: MapPlayerPresence
): MapPlayerPresence[] {
  const index = list.findIndex((item) => item.userId === next.userId)
  if (index < 0) return [...list, next]
  const copy = [...list]
  copy[index] = { ...copy[index], ...next }
  return copy
}

export function removeMapPlayer(
  list: MapPlayerPresence[],
  userId: number
): MapPlayerPresence[] {
  return list.filter((item) => item.userId !== userId)
}

/** Маркер одной точки следа для WebView-карты. */
export type MapTrailMarker = {
  markerId: string
  userId: number
  nickname: string
  lat: number
  lng: number
  ageSec: number
  opacity: number
  affiliationElement?: MapPlayerPresence["affiliationElement"]
  avatarVariant?: number | null
  profileFrameKey?: string | null
}

/** Opacity: свежее (10 мин) ярче, старше (20 мин) бледнее. */
export function trailOpacityForAgeSec(ageSec: number): number {
  if (ageSec <= 10 * 60) return 0.88
  if (ageSec <= 12 * 60) return 0.72
  if (ageSec <= 16 * 60) return 0.55
  return 0.4
}

export function formatTrailAgeLabel(ageSec: number): string {
  // Публичная тень — не раньше 10 минут (не показываем «минуту назад»).
  const minutes = Math.max(10, Math.round(ageSec / 60))
  return `Был здесь ~${minutes} мин назад`
}

/** Разворачивает игроков в маркеры следа (несколько точек на userId). */
export function expandMapPlayersToTrailMarkers(
  players: MapPlayerPresence[]
): MapTrailMarker[] {
  const markers: MapTrailMarker[] = []
  for (const player of players) {
    const rawTrail =
      player.trail && player.trail.length > 0
        ? player.trail
        : Number.isFinite(player.lat) && Number.isFinite(player.lng)
          ? [{ lat: player.lat, lng: player.lng, ageSec: 10 * 60 }]
          : []
    const trail = dedupeTrailPoints(rawTrail)
    for (const point of trail) {
      markers.push({
        markerId: `${player.userId}:${point.ageSec}`,
        userId: player.userId,
        nickname: player.nickname,
        lat: point.lat,
        lng: point.lng,
        ageSec: point.ageSec,
        opacity: trailOpacityForAgeSec(point.ageSec),
        affiliationElement: player.affiliationElement,
        avatarVariant: player.avatarVariant ?? 1,
        profileFrameKey: player.profileFrameKey ?? null,
      })
    }
  }
  return markers
}

/** Если игрок стоит на месте, слоты схлопываются в одну точку. */
function dedupeTrailPoints<T extends { lat: number; lng: number; ageSec: number }>(
  points: T[],
  minDistM = 25
): T[] {
  const freshestFirst = [...points].sort((a, b) => a.ageSec - b.ageSec)
  const kept: T[] = []
  for (const point of freshestFirst) {
    const tooClose = kept.some(
      (existing) =>
        distanceMeters(existing.lat, existing.lng, point.lat, point.lng) < minDistM
    )
    if (tooClose) continue
    kept.push(point)
  }
  return kept.sort((a, b) => b.ageSec - a.ageSec)
}
